import ShopDirectory from './ShopDirectory';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Home,
  Map,
  BriefcaseBusiness,
  Coffee,
  BookOpen,
  MessageCircle,
  Landmark,
  Settings,
  ChevronRight,
  X,
  ArrowUpRight,
  Check,
  Coins,
  Ticket,
  Zap,
  Utensils,
  MapPin,
  Heart,
  Send,
  LogOut,
  CheckCircle2,
  Globe2,
  Leaf,
  Users,
  ShoppingBag,
  Camera,
  LoaderCircle,
  Sparkles,
} from 'lucide-react';
import {
  activities,
  APPEARANCE_PALETTE,
  text,
  careers,
  workProfiles,
  careerLevel,
  DAILY_SHIFTS,
  getLocation,
  goalDefinitions,
  LOCALES,
  utcDay,
  locations,
  npcs,
  zones,
  type Locale,
  type SkillId,
  type ZoneId,
} from '../shared/content';
import type { Character, Snapshot, SocialPost, Coop, Decor } from '../shared/types';
import AdminPanel from './AdminPanel';
import CityJournal from './CityJournal';
import { queuedTranslation, cancelQueuedTranslations } from './translation-queue';
import { npcStoryCopy, backgroundStoryCopy, chapterNotes } from '../shared/narrative-copy';
import AdSlot from './AdSlot';
import { useAdDelivery } from './ads/useAdDelivery';
import AuthRecovery from './AuthRecovery';
import AccountSupport from './AccountSupport';
import PrivacyPanel from './PrivacyPanel';
import BlockedResidents from './BlockedResidents';
import { ShareDialog, PublicShare } from './Sharing';
import { FriendsPanel, CoopPanel, coopStepName } from './SocialPanels';
import { useRoom, type FriendMeetingRequest } from './useRoom';
import CityScene from './scene/CityScene';
import GovernmentPanel from './GovernmentPanel';
import RoomScene from './scene/RoomScene';
import HomePanel from './HomePanel';
import ProjectGarden from './ProjectGarden';
import { projectStage } from '../shared/project-stage';
import { homeComfort } from '../shared/room-rules';
import { api, ApiError, command, getWorld, post } from './api';
import { errorText, initialLocale, languageNames, translate, type Word } from './i18n';
import './styles.css';
type Panel = 'map' | 'home' | 'careers' | 'goals' | 'shops' | 'feed' | 'chat' | 'city' | 'settings' | 'admin';
type User = { id: string; name: string; email: string; emailVerified?: boolean };
const menus: { id: Panel; label: Word; icon: typeof Home }[] = [
  { id: 'map', label: 'map', icon: Map },
  { id: 'home', label: 'home', icon: Home },
  { id: 'careers', label: 'careers', icon: BriefcaseBusiness },
  { id: 'goals', label: 'goals', icon: BookOpen },
  { id: 'shops', label: 'shops', icon: Coffee },
  { id: 'feed', label: 'feed', icon: Users },
  { id: 'chat', label: 'chat', icon: MessageCircle },
  { id: 'city', label: 'city', icon: Landmark },
];
const mobileMenu: Record<Panel, Record<Locale, string>> = {
  map: { en: 'Map', 'zh-Hans': '街区', 'zh-Hant': '街區', es: 'Barrio' },
  home: { en: 'Home', 'zh-Hans': '我的家', 'zh-Hant': '我的家', es: 'Hogar' },
  careers: { en: 'Work', 'zh-Hans': '工作', 'zh-Hant': '工作', es: 'Trabajo' },
  goals: { en: 'Story', 'zh-Hans': '故事', 'zh-Hant': '故事', es: 'Historia' },
  shops: { en: 'Cafés', 'zh-Hans': '小店', 'zh-Hant': '小店', es: 'Cafés' },
  feed: { en: 'Journal', 'zh-Hans': '动态', 'zh-Hant': '動態', es: 'Diario' },
  chat: { en: 'Chat', 'zh-Hans': '聊天', 'zh-Hant': '聊天', es: 'Chat' },
  city: { en: 'City', 'zh-Hans': '城市', 'zh-Hant': '城市', es: 'Ciudad' },
  admin: { en: 'Admin', 'zh-Hans': '管理', 'zh-Hant': '管理', es: 'Admin' },
  settings: { en: 'Settings', 'zh-Hans': '设置', 'zh-Hant': '設定', es: 'Ajustes' },
};
const money = (n: number) => new Intl.NumberFormat().format(n);
const socialFeedPath = (panel: string, sceneId: string) =>
  panel === 'chat' ? `/feed?kind=chat&sceneId=${encodeURIComponent(sceneId)}` : '/feed?kind=post';
export default function App() {
  const [locale, setLocale] = useState<Locale>(initialLocale),
    [user, setUser] = useState<User | null>(null),
    [world, setWorld] = useState<Snapshot | null>(null),
    [ready, setReady] = useState(false),
    [busy, setBusy] = useState(false),
    [panel, setPanel] = useState<Panel>('map'),
    [selected, setSelected] = useState('home'),
    [zone, setZone] = useState<ZoneId>('brooklyn'),
    [toast, setToast] = useState<{ message: string; error: boolean } | null>(null),
    [clock, setClock] = useState(Date.now()),
    [offset, setOffset] = useState(0),
    [dialogCareer, setDialogCareer] = useState<SkillId | null>(null),
    [choice, setChoice] = useState(0),
    [shopName, setShopName] = useState(''),
    [shopPrice, setShopPrice] = useState(18),
    [posts, setPosts] = useState<SocialPost[]>([]),
    [draft, setDraft] = useState(''),
    [visibility, setVisibility] = useState('city'),
    [gov, setGov] = useState<any>(null),
    [reportId, setReportId] = useState<string | null>(null),
    [reportReason, setReportReason] = useState('');
  const [emailAvailable, setEmailAvailable] = useState(false),
    [resetOpen, setResetOpen] = useState(() => new URLSearchParams(location.search).has('reset')),
    [authMode, setAuthMode] = useState<'signup' | 'signin'>('signup');
  const [accountOpen, setAccountOpen] = useState(false);
  const [privacyReceiptOpen, setPrivacyReceiptOpen] = useState(
    () => new URLSearchParams(location.search).get('privacy') === 'receipt',
  );
  const [shareOpen, setShareOpen] = useState(false),
    [publicShare, setPublicShare] = useState<string | null>(() =>
      new URLSearchParams(location.search).get('share'),
    );
  const [coops, setCoops] = useState<Coop[]>([]),
    [socialTab, setSocialTab] = useState<'journal' | 'friends'>('journal'),
    [visit, setVisit] = useState<{ name: string; home: string; decor: Decor[] } | null>(null),
    [preferredRoom, setPreferredRoom] = useState<FriendMeetingRequest>();
  const meetingAttempt = useRef(0);
  const reportedMeeting = useRef('');
  const identityEpoch = useRef(0);
  const actionsInFlight = useRef(0);
  const clearAccount = useCallback(() => {
    identityEpoch.current++;
    actionsInFlight.current = 0;
    setBusy(false);
    cancelQueuedTranslations();
    setWorld(null);
    setUser(null);
    setPosts([]);
    setCoops([]);
    setGov(null);
    setDraft('');
    setVisit(null);
    setPreferredRoom(undefined);
    setShareOpen(false);
    setAccountOpen(false);
    setReportId(null);
    setDialogCareer(null);
    setSelected('home');
    setZone('brooklyn');
    setPanel('map');
    sessionStorage.removeItem('ado-character-id');
    sessionStorage.removeItem('ado-pending-command');
    sessionStorage.removeItem('ado-reconnection');
  }, []);
  const t = (key: Word) => translate(locale, key);
  const char = world?.character;
  const p = char?.data;
  const adContext =
    char && !publicShare && !resetOpen && !accountOpen && !privacyReceiptOpen
      ? { actorId: char.id, cityId: char.cityId, zone: char.data.zone, projectId: world?.project.id }
      : undefined;
  const subwayAd = useAdDelivery('subway', adContext);
  const cafeAd = useAdDelivery('cafe', adContext);
  const journalAd = useAdDelivery('journal', adContext);
  const communityAd = useAdDelivery('community', adContext);
  const place = getLocation(selected)!;
  const current = getLocation(p?.location || 'home')!;
  const autoFinish = useRef(false);
  const tRef = useRef(t);
  tRef.current = t;
  const multiplayer = useRoom(
    publicShare || resetOpen || accountOpen || privacyReceiptOpen ? undefined : char,
    preferredRoom,
  );
  const notify = (message: string, error = false) => setToast({ message, error });
  useEffect(() => {
    const result = multiplayer.meetingResult;
    if (
      !result ||
      result.characterId !== char?.id ||
      result.attempt !== meetingAttempt.current ||
      result.targetId !== preferredRoom?.targetId
    )
      return;
    const key = `${result.characterId}:${result.attempt}`;
    if (reportedMeeting.current === key) return;
    reportedMeeting.current = key;
    if (result.outcome === 'joined') {
      notify(
        text(
          'You joined your friend’s scene.',
          '已进入好友所在场景。',
          '已進入好友所在場景。',
          'Entraste en la escena de tu amigo.',
        )[locale],
      );
      // A full or cancelled target must never move the resident before a handoff succeeds.
      if (p?.zone !== result.zone && getLocation(result.location)) void go(result.location);
      return;
    }
    const message =
      result.reason === 'unsupported'
        ? result.outcome === 'fallback'
          ? text(
              'This server version cannot arrange this meeting yet. You stayed in your scene; try again later.',
              '当前服务器版本暂不支持这次会合，你仍在原场景，可稍后重试。',
              '目前伺服器版本暫不支援這次會合，你仍在原場景，可稍後重試。',
              'Esta versión aún no admite el encuentro. Sigues en tu escena; inténtalo más tarde.',
            )
          : text(
              'This server version cannot arrange this meeting yet. Try again later.',
              '当前服务器版本暂不支持这次会合，请稍后重试。',
              '目前伺服器版本暫不支援這次會合，請稍後重試。',
              'Esta versión aún no admite el encuentro. Inténtalo más tarde.',
            )
        : result.outcome === 'fallback'
          ? result.reason === 'full'
            ? text(
                'Your friend’s scene is full. You stayed in your scene; try meeting again when a place opens.',
                '好友场景暂时满员，你仍在原场景；有空位后可再次点击会合。',
                '好友場景暫時滿員，你仍在原場景；有空位後可再次點擊會合。',
                'La escena de tu amigo está llena. Sigues en tu escena; inténtalo cuando haya sitio.',
              )
            : text(
                'Your friend is unavailable. You stayed in your scene and can try again later.',
                '好友暂时无法会合，你仍在原场景，可以稍后重试。',
                '好友暫時無法會合，你仍在原場景，可以稍後重試。',
                'Tu amigo no está disponible. Sigues en tu escena y puedes volver a intentarlo.',
              )
          : multiplayer.status === 'online' && multiplayer.roomId === result.roomId
            ? text(
                'Unable to confirm the meeting. You are still in your current scene and can try again.',
                '暂时无法确认会合，你仍在当前场景，可以稍后重试。',
                '暫時無法確認會合，你仍在目前場景，可以稍後重試。',
                'No se pudo confirmar el encuentro. Sigues en tu escena actual y puedes volver a intentarlo.',
              )
            : text(
                'Unable to confirm the meeting. Try again shortly.',
                '暂时无法确认好友会合，请稍后重试。',
                '暫時無法確認好友會合，請稍後重試。',
                'No se pudo confirmar el encuentro. Inténtalo de nuevo en un momento.',
              );
    notify(message[locale], result.outcome === 'unavailable');
  }, [
    multiplayer.meetingResult,
    multiplayer.status,
    multiplayer.roomId,
    char?.id,
    locale,
    preferredRoom?.targetId,
  ]);
  useEffect(() => {
    const ended = (event: Event) => {
      clearAccount();
      setAuthMode('signin');
      const restricted = (event as CustomEvent).detail?.restricted;
      notify(
        restricted
          ? errorText(locale, new ApiError('ACCOUNT_RESTRICTED', 'Account restricted.', 403))
          : text(
              'Your session ended. Please sign in again.',
              '会话已结束，请重新登录。',
              '連線階段已結束，請重新登入。',
              'Tu sesión ha terminado. Inicia sesión de nuevo.',
            )[locale],
        true,
      );
    };
    window.addEventListener('ado-session-ended', ended);
    return () => window.removeEventListener('ado-session-ended', ended);
  }, [locale]);
  const refresh = useCallback(async () => {
    const epoch = identityEpoch.current;
    let w: Snapshot;
    try {
      w = await getWorld();
    } catch (error) {
      if (error instanceof ApiError && error.code === 'ACCOUNT_RESTRICTED') {
        setAccountOpen(true);
      }
      if (error instanceof ApiError && error.code === 'UNAUTHORIZED') clearAccount();
      throw error;
    }
    if (epoch !== identityEpoch.current)
      throw new ApiError('SESSION_CHANGED', 'The signed-in account changed.', 409);
    const old = sessionStorage.getItem('ado-character-id');
    if (old && old !== w.character.id) {
      cancelQueuedTranslations();
      setPosts([]);
      setCoops([]);
      setGov(null);
      setDraft('');
      setVisit(null);
      setPreferredRoom(undefined);
      setShareOpen(false);
      setPanel('map');
      api('/session')
        .then((s) => setUser(s.user))
        .catch(() => {});
    }
    setWorld(w);
    sessionStorage.setItem('ado-character-id', w.character.id);
    setOffset(Date.parse(w.serverTime) - Date.now());
    setZone(w.character.data.zone);
    return w;
  }, []);
  useEffect(() => {
    let alive = true;
    const changed = async (event: StorageEvent) => {
      if (event.key !== 'ado-session-change') return;
      clearAccount();
      const epoch = identityEpoch.current;
      try {
        const session = await api('/session');
        if (!alive || epoch !== identityEpoch.current) return;
        setUser(session.user);
        setEmailAvailable(session.emailAvailable);
        if (session.characterId) {
          const w = await refresh();
          if (alive && epoch === identityEpoch.current) {
            setLocale(w.character.data.locale);
            setSelected(w.character.data.location);
          }
        }
      } catch {
        /* The signed-out shell is already private; next sign-in can retry. */
      }
    };
    window.addEventListener('storage', changed);
    return () => {
      alive = false;
      window.removeEventListener('storage', changed);
    };
  }, [clearAccount, refresh]);
  useEffect(() => {
    let alive = true;
    api('/session')
      .then(async (s) => {
        if (!alive) return;
        setUser(s.user);
        setEmailAvailable(s.emailAvailable);
        if (s.characterId) {
          const w = await refresh();
          if (alive) {
            setLocale(w.character.data.locale);
            setSelected(w.character.data.location);
          }
        }
      })
      .catch((e) => notify(errorText(locale, e), true))
      .finally(() => {
        if (alive) setReady(true);
      });
    return () => {
      alive = false;
    };
  }, []);
  useEffect(() => {
    document.documentElement.lang = locale;
    localStorage.setItem('ado-locale', locale);
  }, [locale]);
  useEffect(() => {
    const id = setInterval(() => setClock(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 6500);
    return () => clearTimeout(id);
  }, [toast]);
  const act = useCallback(
    async (type: string, payload: Record<string, unknown> = {}, message?: string) => {
      const epoch = identityEpoch.current;
      actionsInFlight.current++;
      setBusy(true);
      try {
        const result = await command(type, payload);
        if (epoch !== identityEpoch.current) return null;
        await refresh();
        if (epoch !== identityEpoch.current) return null;
        if (message) notify(message);
        return result;
      } catch (e) {
        if (epoch !== identityEpoch.current) return null;
        notify(errorText(locale, e), true);
        await refresh().catch(() => {});
        return null;
      } finally {
        if (epoch === identityEpoch.current) {
          actionsInFlight.current--;
          setBusy(actionsInFlight.current > 0);
        }
      }
    },
    [locale, refresh],
  );
  useEffect(() => {
    if (!p?.travel || clock + offset < Date.parse(p.travel.readyAt) || autoFinish.current) return;
    autoFinish.current = true;
    act('travel.finish', { travelId: p.travel.id }).finally(() => {
      autoFinish.current = false;
    });
  }, [clock, p?.travel, offset, act]);
  useEffect(() => {
    if (!char) return;
    let active = true;
    if (panel === 'chat') setPosts([]);
    const load = () => {
      if (document.visibilityState === 'hidden') return;
      if (panel === 'feed' || (panel === 'chat' && multiplayer.status === 'online' && multiplayer.roomId))
        api(socialFeedPath(panel, multiplayer.roomId))
          .then((x) => {
            if (active) setPosts(x.posts);
          })
          .catch(() => {});
      if (panel === 'city')
        api('/government')
          .then((x) => {
            if (active) setGov(x);
          })
          .catch(() => {});
      if (panel === 'city' && document.visibilityState === 'visible')
        api('/project')
          .then((project) => {
            if (active) setWorld((w) => (w && w.character.id === char.id ? { ...w, project } : w));
          })
          .catch(() => {});
    };
    load();
    const id = setInterval(load, panel === 'chat' ? 5000 : 15000);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [panel, char?.id, multiplayer.roomId, multiplayer.status]);
  useEffect(() => {
    let active = true;
    if (char)
      api('/cooperations')
        .then((r) => {
          if (active) setCoops(r.cooperations);
        })
        .catch(() => {});
    return () => {
      active = false;
    };
  }, [char?.id, char?.revision]);
  useEffect(() => {
    if (multiplayer.newsEpoch && char) refresh().catch(() => {});
  }, [multiplayer.newsEpoch]);
  useEffect(() => {
    if (world?.shop) {
      setShopName(world.shop.name);
      setShopPrice(world.shop.price);
    }
  }, [world?.shop?.id]);
  const go = async (id: string) => {
    setSelected(id);
    const loc = getLocation(id)!;
    setZone(loc.zone);
    if (!char || p?.location === id || p?.travel || p?.work) return;
    await act('travel.start', { destination: id });
  };
  const openPanel = (id: Panel) => {
    setPanel(id);
    if (id === 'home' && p?.location !== 'home' && !p?.travel && !p?.work) go('home');
  };
  const changeLocale = async (next: Locale) => {
    setLocale(next);
    if (char) await act('settings', { locale: next });
  };
  const work = () => {
    const w = p?.work;
    if (!w) return null;
    const remaining = Math.max(0, Math.min(45, Math.ceil((Date.parse(w.readyAt) - clock - offset) / 1000)));
    return (
      <div className="work-banner">
        <div className="work-orb">
          <Coffee size={21} />
        </div>
        <div>
          <small>{t('working')}</small>
          <strong>{careers.find((c) => c.id === w.career)?.name[locale]}</strong>
          <div className="meter">
            <span style={{ width: `${Math.min(100, ((45 - remaining) / 45) * 100)}%` }} />
          </div>
        </div>
        {clock + offset > Date.parse(w.startedAt) + 86400000 ? (
          <button className="button soft compact" disabled={busy} onClick={() => act('work.cancel')}>
            {t('cancelWork')}
          </button>
        ) : remaining > 0 ? (
          <b className="countdown">{remaining}s</b>
        ) : (
          <button
            className="button primary compact"
            disabled={busy}
            onClick={() => act('work.finish', { workId: w.id }, t('earned'))}
          >
            {t('finish')} <ArrowUpRight size={16} />
          </button>
        )}
      </div>
    );
  };
  const activeCoop = char
    ? [...coops]
        .sort((a, b) => Number(b.creatorId === char.id) - Number(a.creatorId === char.id))
        .find(
          (j) =>
            !j.claimed[char.id] &&
            ((j.completedAt && (j.contributions[char.id]?.length || 0) === 3) ||
              Date.parse(j.expiresAt) > clock),
        )
    : undefined;
  const nextCoopIndex = activeCoop && char ? activeCoop.contributions[char.id]?.length || 0 : 0;
  const goalReady = (id: string) => {
    if (!p) return false;
    return (
      {
        'first-shift': p.counters.shifts >= 1,
        'settle-in': p.counters.meals > 0 && p.counters.rests > 0,
        'make-it-yours': p.decor.length > 0,
        'city-explorer': p.visited.length >= 3,
        'good-neighbor': p.counters.helps + p.counters.contributions > 0,
        'next-chapter': Object.values(p.skills).some((x) => x >= 80),
        'open-door': !!world?.shop,
      }[id] || false
    );
  };
  const onVisit = async (id: string) => {
    try {
      setVisit(await api(`/homes/${id}`));
    } catch (e) {
      notify(errorText(locale, e), true);
    }
  };
  const onMeet = async (id: string) => {
    if (!char) return;
    const attempt = ++meetingAttempt.current;
    setPanel('map');
    notify(
      text(
        'Trying to join your friend’s scene…',
        '正在尝试进入好友场景…',
        '正在嘗試進入好友場景…',
        'Intentando entrar en la escena de tu amigo…',
      )[locale],
    );
    setPreferredRoom({ targetId: id, attempt });
  };
  const locationActions = () => (
    <>
      <p className="muted">{current.description[locale]}</p>
      <div className="action-grid">
        {current.actions.map((id) => {
          if (id in activities) {
            const a = activities[id as keyof typeof activities];
            const cd = Math.max(
              0,
              Math.ceil(((p?.cooldowns[`activity:${id}`] || 0) - clock - offset) / 1000),
            );
            return (
              <button
                className="action-tile"
                key={id}
                disabled={busy || !!p?.work || !!p?.travel || cd > 0}
                onClick={() => act('activity', { id }, t('saved'))}
              >
                <span>{a.name[locale]}</span>
                <small>
                  {cd ? `${cd}s` : a.cost ? `C$${a.cost}` : text('Free', '免费', '免費', 'Gratis')[locale]} ·{' '}
                  {a.energy + (id === 'rest' && p ? homeComfort(p).rest : 0) > 0 ? '+' : ''}
                  {a.energy + (id === 'rest' && p ? homeComfort(p).rest : 0)} <Zap size={11} />
                  {' · '}
                  {a.hunger + (id === 'cook' && p ? homeComfort(p).cook : 0) > 0 ? '+' : ''}
                  {a.hunger + (id === 'cook' && p ? homeComfort(p).cook : 0)} <Utensils size={11} />
                </small>
              </button>
            );
          }
          if (id.startsWith('work-'))
            return (
              <button
                className="action-tile accent"
                key={id}
                disabled={busy || !!p?.work || !!p?.travel}
                onClick={() => {
                  setDialogCareer(id.replace('work-', '') as SkillId);
                  setChoice(0);
                }}
              >
                <span>{t('shift')}</span>
                <small>
                  <BriefcaseBusiness size={12} /> C$100–150
                </small>
              </button>
            );
          const target: Record<string, Panel> = {
            decorate: 'home',
            careers: 'careers',
            community: 'city',
            government: 'city',
          };
          if (target[id])
            return (
              <button className="action-tile" key={id} onClick={() => openPanel(target[id]!)}>
                {t(target[id] as Word)}
                <ChevronRight size={15} />
              </button>
            );
          if (id === 'photo')
            return (
              <button className="action-tile" key={id} onClick={share}>
                <span>{t('photo')}</span>
                <Camera size={16} />
              </button>
            );
          return null;
        })}
      </div>
      {coops
        .filter(
          (j) =>
            (!j.completedAt || j.npcPartner) &&
            (j.contributions[char!.id]?.length || 0) < 3 &&
            Date.parse(j.expiresAt) > Date.now(),
        )
        .map((j) => {
          const n = j.contributions[char!.id]?.length || 0;
          const step = ['gather', 'prepare', 'deliver'][n];
          const placeId = ['bodega', 'home', 'park'][n];
          return placeId === p?.location ? (
            <button
              className="button soft full coop-context"
              key={j.id}
              disabled={busy || !!p?.work || !!p?.travel}
              onClick={() => act('coop.step', { coopId: j.id, step }, t('saved'))}
            >
              <Heart size={14} />
              {coopStepName(locale, step!)} · 3 <Zap size={12} />
            </button>
          ) : null;
        })}
      {npcs
        .filter((n) => n.location === p?.location)
        .map((n) => (
          <div className="npc-card" key={n.id}>
            <span className="avatar-disc" style={{ background: n.color }}>
              {n.name[0]}
            </span>
            <div>
              <strong>{n.name}</strong>
              <small>{n.role[locale]}</small>
              <p>
                “
                {
                  npcStoryCopy[n.id as keyof typeof npcStoryCopy][
                    (p?.npcRelations[n.id] || 0) >= 5
                      ? 'familiar'
                      : (p?.npcRelations[n.id] || 0) > 0
                        ? 'progress'
                        : 'intro'
                  ][locale]
                }
                ”
              </p>
              <button
                className="text-button"
                disabled={busy}
                onClick={() => act('npc.talk', { npcId: n.id }, t('saved'))}
              >
                {t('talk')} <Heart size={13} /> {p?.npcRelations[n.id] || 0}
              </button>
            </div>
          </div>
        ))}
    </>
  );
  function share() {
    if (char) setShareOpen(true);
  }
  async function submitPost() {
    if (!draft.trim()) return;
    const r = await act(
      'post.create',
      {
        kind: panel === 'chat' ? 'chat' : 'post',
        body: draft,
        language: locale,
        visibility,
        sceneId: panel === 'chat' ? multiplayer.roomId : undefined,
      },
      t('saved'),
    );
    if (r) {
      setDraft('');
      setPosts((await api(socialFeedPath(panel, multiplayer.roomId))).posts);
    }
  }
  const panelBody = () => {
    if (!p || !char || !world) return null;
    if (panel === 'admin') return <AdminPanel locale={locale} onMessage={notify} />;
    if (panel === 'home')
      return (
        <HomePanel
          character={char}
          locale={locale}
          busy={busy}
          onAction={act}
          onGoHome={() => {
            go('home');
            setPanel('map');
          }}
        />
      );
    if (panel === 'careers')
      return (
        <>
          <div className="section-eyebrow">SMALL STEPS, BIG DREAMS</div>
          <h2>{t('careers')}</h2>
          <p className="muted small">{t('shiftInfo')}</p>
          <div className="quota">
            <BriefcaseBusiness size={17} />
            {p.daily.shifts} / {DAILY_SHIFTS}
            <span>{t('shift')}</span>
          </div>
          {careers.map((c) => {
            const lv = careerLevel(p.skills[c.id]);
            const loc = c.id === 'service' ? 'cafe' : c.id === 'logic' ? 'jobs' : 'studio';
            return (
              <article
                className="career-card"
                key={c.id}
                style={{ '--career-color': c.color } as React.CSSProperties}
              >
                <div className="career-icon">
                  {c.id === 'service' ? <Coffee /> : c.id === 'logic' ? <BriefcaseBusiness /> : <Sparkles />}
                </div>
                <div>
                  <h3>{c.name[locale]}</h3>
                  <p>{c.stages[lv - 1]![locale]}</p>
                  <small>
                    {t('level')} {lv} · {p.skills[c.id]} {t('xp')}
                  </small>
                  <div className="meter">
                    <span
                      style={{
                        width: `${lv === 3 ? 100 : lv === 2 ? ((p.skills[c.id] - 80) / 220) * 100 : (p.skills[c.id] / 80) * 100}%`,
                      }}
                    />
                  </div>
                </div>
                <button
                  className="text-button"
                  disabled={busy || !!p.work}
                  onClick={() => {
                    go(loc);
                    setPanel('map');
                  }}
                >
                  {t('go')}
                  <ArrowUpRight size={15} />
                </button>
              </article>
            );
          })}
          {work()}
        </>
      );
    if (panel === 'goals')
      return (
        <>
          <div className="section-eyebrow">ONE CHAPTER AT A TIME</div>
          <h2>{t('goals')}</h2>
          <p className="muted">{backgroundStoryCopy[p.background].welcome[locale]}</p>
          {goalDefinitions.map((g, i) => (
            <article className={`goal-row ${p.goals.includes(g.id) ? 'done' : ''}`} key={g.id}>
              <span className="goal-number">
                {p.goals.includes(g.id) ? <Check size={18} /> : String(i + 1).padStart(2, '0')}
              </span>
              <div>
                <h3>{g.name[locale]}</h3>
                <p>{g.description[locale]}</p>
                {p.goals.includes(g.id) ? (
                  <>
                    <small className="green">{t('completed')}</small>
                    <p className="chapter-note">{chapterNotes[g.id as keyof typeof chapterNotes][locale]}</p>
                  </>
                ) : (
                  <button
                    className="text-button"
                    disabled={busy || !goalReady(g.id)}
                    onClick={() => act('goal.claim', { goalId: g.id }, t('saved'))}
                  >
                    {t('claim')} <Coins size={13} />
                  </button>
                )}
              </div>
            </article>
          ))}
          <CityJournal events={world.events} character={char} locale={locale} kind="personal" />
        </>
      );
    if (panel === 'shops')
      return (
        <>
          <div className="section-eyebrow">MADE IN YOUR NEIGHBORHOOD</div>
          <h2>{t('shops')}</h2>
          {world.shop ? (
            <div className="shop-owner">
              <div className="shop-illustration">
                <Coffee size={40} />
                <span>{world.shop.name}</span>
              </div>
              <div className="shop-stats">
                <div>
                  <b>{world.shop.stock}</b>
                  <small>{t('stock')}</small>
                </div>
                <div>
                  <b>{world.shop.sold}</b>
                  <small>{t('sold')}</small>
                </div>
                <div>
                  <b>C${money(world.shop.revenue)}</b>
                  <small>{t('cash')}</small>
                </div>
              </div>
              <label>
                {t('shopName')}
                <input value={shopName} maxLength={36} onChange={(e) => setShopName(e.target.value)} />
              </label>
              <label>
                {t('price')}
                <select value={shopPrice} onChange={(e) => setShopPrice(Number(e.target.value))}>
                  {[12, 18, 24].map((n) => (
                    <option key={n} value={n}>
                      C${n}
                    </option>
                  ))}
                </select>
              </label>
              <button
                className="button light full"
                disabled={busy}
                onClick={() => act('shop.configure', { name: shopName, price: shopPrice }, t('saved'))}
              >
                {t('save')}
              </button>
              <div className="stack">
                <button
                  className="button primary full"
                  disabled={busy}
                  onClick={() => act('shop.restock', { quantity: 10 }, t('saved'))}
                >
                  {t('restock')}
                </button>
                <button
                  className="button soft full"
                  disabled={busy}
                  onClick={() => act('shop.npc-orders', {}, t('saved'))}
                >
                  {t('npcOrders')}
                </button>
              </div>
              <p className="muted small">{t('npcDemand')}</p>
            </div>
          ) : (
            <div className="empty-card">
              <Coffee size={38} />
              <h3>{t('openShop')}</h3>
              <p>{t('shopCost')}</p>
              <input
                aria-label={t('shopName')}
                placeholder={t('shopName')}
                value={shopName}
                onChange={(e) => setShopName(e.target.value)}
                maxLength={36}
              />
              <button
                className="button primary full"
                disabled={busy || shopName.trim().length < 2}
                onClick={() =>
                  p.location === 'cafe' ? act('shop.open', { name: shopName }, t('saved')) : go('cafe')
                }
              >
                {p.location === 'cafe' ? t('open') : t('go')}
              </button>
            </div>
          )}
          <p className="muted tiny">
            {
              text(
                `Earned cash for player purchases: C$${char.tradableCash}`,
                `可在玩家店消费的已赚现金：C$${char.tradableCash}`,
                `可在玩家店消費的已賺現金：C$${char.tradableCash}`,
                `Efectivo ganado para compras a jugadores: C$${char.tradableCash}`,
              )[locale]
            }
          </p>
          <ShopDirectory
            key={`${char.id}:${char.cityId}`}
            character={char}
            locale={locale}
            busy={busy}
            onBuy={(shopId) => {
              void act('shop.buy', { shopId }, t('saved'));
            }}
          />
        </>
      );
    if (panel === 'feed' && socialTab === 'friends')
      return (
        <>
          <div className="social-tabs">
            <button onClick={() => setSocialTab('journal')}>{t('feed')}</button>
            <button className="active">{text('Friends', '好友', '好友', 'Amigos')[locale]}</button>
          </div>
          <FriendsPanel
            locale={locale}
            revision={char.revision}
            onAction={act}
            onVisit={onVisit}
            onMeet={onMeet}
            nearby={multiplayer.peers}
          />
        </>
      );
    if (panel === 'feed' || panel === 'chat')
      return (
        <>
          {panel === 'feed' && (
            <div className="social-tabs">
              <button className="active">{t('feed')}</button>
              <button onClick={() => setSocialTab('friends')}>
                {text('Friends', '好友', '好友', 'Amigos')[locale]}
              </button>
            </div>
          )}
          <div className="section-eyebrow">
            {panel === 'chat'
              ? zones.find((z) => z.id === p.zone)?.name[locale]
              : 'LITTLE MOMENTS, REAL STORIES'}
          </div>
          <h2>{t(panel)}</h2>
          {panel === 'chat' && (
            <p className="muted">
              {
                (multiplayer.status === 'online'
                  ? text(
                      'Only people in this scene · up to 40 residents. City posts reach the whole city.',
                      '仅当前场景的居民可见，最多40人。城市动态面向全城。',
                      '僅目前場景的居民可見，最多40人。城市動態面向全城。',
                      'Solo esta escena, hasta 40 residentes. El diario llega a toda la ciudad.',
                    )
                  : text(
                      'Reconnect to the scene to chat. Your draft will stay here.',
                      '场景连接恢复后即可聊天，草稿会保留。',
                      '場景連線恢復後即可聊天，草稿會保留。',
                      'Vuelve a conectar para chatear. Tu borrador se conserva.',
                    ))[locale]
              }
            </p>
          )}
          <div className="composer">
            <textarea
              value={draft}
              maxLength={500}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={t('shareThought')}
            />
            <div>
              <small>{draft.length}/500</small>
              <select
                aria-label={t('visibility')}
                value={visibility}
                onChange={(e) => setVisibility(e.target.value)}
              >
                <option value="city">
                  {panel === 'chat'
                    ? text('This scene', '当前场景', '目前場景', 'Esta escena')[locale]
                    : t('everyone')}
                </option>
                <option value="friends">{t('friendsOnly')}</option>
              </select>
              <button
                className="button primary compact"
                disabled={busy || !draft.trim() || (panel === 'chat' && multiplayer.status !== 'online')}
                onClick={submitPost}
              >
                <Send size={14} />
                {t(panel === 'chat' ? 'send' : 'publish')}
              </button>
            </div>
          </div>
          {posts.length ? (
            posts.map((item) => (
              <PostCard
                key={`${item.id}:${item.version}`}
                item={item}
                locale={locale}
                readingLocale={p.readingLocale}
                currentId={char.id}
                busy={busy}
                onAction={async (type, payload) => {
                  if (type === 'report') {
                    setReportId(item.id);
                    return;
                  }
                  const result = await act(type, payload, t('saved'));
                  if (!result) return false;
                  try {
                    setPosts((await api(socialFeedPath(panel, multiplayer.roomId))).posts);
                  } catch (error) {
                    notify(errorText(locale, error), true);
                  }
                  return true;
                }}
              />
            ))
          ) : (
            <div className="empty-card">
              <BookOpen size={32} />
              <p>{t('emptyFeed')}</p>
            </div>
          )}
        </>
      );
    if (panel === 'city')
      return (
        <>
          <div className="section-eyebrow">GROW SOMETHING TOGETHER</div>
          <h2>{t('city')}</h2>
          <ProjectGarden project={world.project} locale={locale} />
          <h3>{t('community')}</h3>
          <p className="muted">{t('communityInfo')}</p>
          <AdSlot
            placement="community"
            locale={locale}
            delivery={communityAd}
            project={{ id: world.project.id, title: world.project.title }}
          />
          <div className="project-stats">
            <b>
              {world.project.progress} <span>/ {world.project.target}</span>
            </b>
            <span>
              {world.project.personalContribution} <Heart size={13} />
            </span>
          </div>
          <div className="meter big">
            <span style={{ width: `${(world.project.progress / world.project.target) * 100}%` }} />
          </div>
          <p className="muted small">
            {text('Today’s contributions', '今日贡献', '今日貢獻', 'Aportes de hoy')[locale]} ·{' '}
            {world.project.personalToday}/3 ·{' '}
            {
              text(
                'Resets at 00:00 UTC',
                '每日 UTC 00:00 重置',
                '每日 UTC 00:00 重設',
                'Se reinicia a las 00:00 UTC',
              )[locale]
            }
          </p>
          <button
            className="button primary full"
            disabled={busy || world.project.completed || world.project.personalToday >= 3 || !!p.travel}
            onClick={() =>
              ['park', 'city-hall'].includes(p.location)
                ? act('community.contribute', {}, t('saved'))
                : go('park')
            }
          >
            {world.project.completed
              ? t('completed')
              : ['park', 'city-hall'].includes(p.location)
                ? t('contribute')
                : t('go')}
          </button>
          {gov?.mode !== 'elected' && (
            <div className="civic-card">
              <Landmark size={30} />
              <h3>{t('caretaker')}</h3>
              <p>{t('caretakerInfo')}</p>
              <div className="policy">
                <span>{t('tax')}</span>
                <b>{gov?.taxRate ?? '…'}%</b>
              </div>
              {gov && (
                <small>
                  {gov.eligibleResidents} / 30{' '}
                  {
                    text('eligible residents', '符合条件的居民', '符合條件的居民', 'residentes elegibles')[
                      locale
                    ]
                  }
                </small>
              )}
            </div>
          )}
          <section className="city-journal">
            <h3>
              <BookOpen size={17} />
              {text('The city newspaper', '城市报', '城市報', 'El periódico de la ciudad')[locale]}
            </h3>
            <a href={`/news?lang=${locale}`} target="_blank" rel="noopener noreferrer">
              {
                text(
                  'Read confirmed city articles',
                  '阅读城市新闻文章',
                  '閱讀城市新聞文章',
                  'Leer artículos de la ciudad',
                )[locale]
              }
            </a>
            <AdSlot placement="journal" locale={locale} delivery={journalAd} />
          </section>
          <CityJournal events={world.events} character={char} locale={locale} kind="city" />
          <p className="muted tiny">
            {locale.startsWith('zh')
              ? locale === 'zh-Hant'
                ? '本週目標按專案開始時的活躍人數鎖定'
                : '本周目标按项目开始时的活跃人数锁定'
              : locale === 'es'
                ? 'La meta semanal se fija al comenzar'
                : 'Weekly target is fixed at project start'}{' '}
            · {world.project.populationBracket}
          </p>
          <CoopPanel
            locale={locale}
            character={char}
            jobs={coops}
            busy={busy}
            onAction={act}
            onGo={(id) => {
              go(id);
              setPanel('map');
            }}
          />
          <GovernmentPanel
            gov={gov}
            locale={locale}
            characterId={char.id}
            onRefresh={async () => setGov(await api('/government'))}
            onMessage={notify}
          />
        </>
      );
    if (panel === 'settings')
      return (
        <>
          <div className="section-eyebrow">MAKE YOURSELF AT HOME</div>
          <h2>{t('settings')}</h2>
          {user && !user.emailVerified && (
            <div className="verify-card">
              <p>
                {
                  text(
                    'Verify your email to work toward in-game election eligibility.',
                    '验证邮箱后可逐步解锁游戏内选举资格。',
                    '驗證電子郵件後可逐步解鎖遊戲內選舉資格。',
                    'Verifica tu correo para obtener elegibilidad electoral en el juego.',
                  )[locale]
                }
              </p>
              <button
                className="button soft compact"
                disabled={!emailAvailable || busy}
                onClick={async () => {
                  try {
                    await post('/auth/send-verification-email', {
                      email: user.email,
                      callbackURL: location.origin,
                    });
                    notify(
                      text(
                        'Verification email queued',
                        '验证邮件已排队发送',
                        '驗證郵件已排隊傳送',
                        'Verificación en cola',
                      )[locale],
                    );
                  } catch (e) {
                    notify(errorText(locale, e), true);
                  }
                }}
              >
                {emailAvailable
                  ? text('Send verification email', '发送验证邮件', '傳送驗證郵件', 'Enviar verificación')[
                      locale
                    ]
                  : text(
                      'Email temporarily unavailable',
                      '邮件服务暂不可用',
                      '郵件服務暫不可用',
                      'Correo no disponible',
                    )[locale]}
              </button>
            </div>
          )}
          {(world.operationsAvailable ?? world.isAdmin) && (
            <button className="button soft full" onClick={() => setPanel('admin')}>
              {text('City operations', '城市运营后台', '城市營運後台', 'Operaciones')[locale]}
            </button>
          )}
          <label>
            {t('uiLanguage')}
            <select value={locale} onChange={(e) => changeLocale(e.target.value as Locale)}>
              {LOCALES.map((l) => (
                <option key={l} value={l}>
                  {languageNames[l]}
                </option>
              ))}
            </select>
          </label>
          <label>
            {t('readingLanguage')}
            <select
              value={p.readingLocale}
              onChange={(e) => act('settings', { readingLocale: e.target.value }, t('saved'))}
            >
              {LOCALES.map((l) => (
                <option key={l} value={l}>
                  {languageNames[l]}
                </option>
              ))}
            </select>
          </label>
          <p className="muted small">{t('translationNote')}</p>
          <label>
            {
              text(
                'Who can visit your home',
                '谁可以来家里做客',
                '誰可以來家裡作客',
                'Quién puede visitar tu hogar',
              )[locale]
            }
            <select
              value={p.homeVisibility || 'friends'}
              onChange={(e) => act('settings', { homeVisibility: e.target.value }, t('saved'))}
            >
              <option value="friends">{t('friendsOnly')}</option>
              <option value="city">{t('everyone')}</option>
              <option value="private">{text('Only me', '仅自己', '僅自己', 'Solo yo')[locale]}</option>
            </select>
          </label>
          <button className="button soft full" onClick={() => setAccountOpen(true)}>
            {text('Account support', '账号支持与申诉', '帳號支援與申訴', 'Soporte de cuenta')[locale]}
          </button>
          <p>
            <a href={`/privacy?lang=${locale}`} target="_blank" rel="noopener noreferrer">
              {text('Data and privacy', '数据与隐私说明', '資料與隱私說明', 'Datos y privacidad')[locale]}
            </a>
          </p>
          <BlockedResidents locale={locale} onAction={act} />
          <h3>{t('appearance')}</h3>
          {(['shirt', 'skin', 'hair', 'trousers'] as const).map((part) => (
            <div key={part}>
              <small>
                {
                  {
                    shirt: text('Shirt', '上衣', '上衣', 'Camiseta'),
                    skin: text('Skin tone', '肤色', '膚色', 'Tono de piel'),
                    hair: text('Hair', '发色', '髮色', 'Cabello'),
                    trousers: text('Trousers', '裤装', '褲裝', 'Pantalón'),
                  }[part][locale]
                }
              </small>
              <div className="swatches">
                {APPEARANCE_PALETTE[part].map((color) => (
                  <button
                    key={color}
                    aria-label={`${part} ${color}`}
                    aria-pressed={p.appearance[part] === color}
                    style={{ background: color }}
                    disabled={busy}
                    onClick={() => act('settings', { appearance: { ...p.appearance, [part]: color } })}
                  >
                    {p.appearance[part] === color && <Check size={18} />}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <button className="button light full" onClick={share}>
            <Camera size={16} />
            {t('photo')}
          </button>
          <button
            className="button danger full"
            onClick={async () => {
              await post('/auth/sign-out', {});
              sessionStorage.removeItem('ado-pending-command');
              sessionStorage.removeItem('ado-character-id');
              clearAccount();
            }}
          >
            <LogOut size={16} />
            {t('signOut')}
          </button>
        </>
      );
    return null;
  };
  if (privacyReceiptOpen)
    return (
      <div className="public-share-page account-support">
        <a className="public-share-brand" href="/">
          AMERICAN DREAM <small>ONLINE · NEW YORK</small>
        </a>
        <label>
          {t('uiLanguage')}
          <select value={locale} onChange={(e) => setLocale(e.target.value as Locale)}>
            {LOCALES.map((lang) => (
              <option key={lang} value={lang}>
                {languageNames[lang]}
              </option>
            ))}
          </select>
        </label>
        <PrivacyPanel locale={locale} mode="receipt" />
        <button
          className="button soft"
          onClick={() => {
            setPrivacyReceiptOpen(false);
            const url = new URL(location.href);
            url.searchParams.delete('privacy');
            history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
          }}
        >
          {text('Back to the game', '返回游戏', '返回遊戲', 'Volver al juego')[locale]}
        </button>
      </div>
    );
  if (accountOpen)
    return (
      <AccountSupport
        locale={locale}
        onClose={async () => {
          try {
            await refresh();
          } catch (e) {
            if (!(e instanceof ApiError && e.code === 'NO_CHARACTER')) throw e;
          }
          setAccountOpen(false);
        }}
        onSignOut={async () => {
          await post('/auth/sign-out', {});
          clearAccount();
        }}
      />
    );
  if (resetOpen)
    return (
      <AuthRecovery
        token={new URLSearchParams(location.search).get('token')}
        locale={locale}
        onComplete={(changed) => {
          setResetOpen(false);
          if (changed) {
            clearAccount();
            setAuthMode('signin');
            sessionStorage.removeItem('ado-pending-command');
            sessionStorage.removeItem('ado-character-id');
          }
          history.replaceState({}, '', location.pathname);
        }}
        onMessage={notify}
      />
    );
  if (publicShare)
    return (
      <PublicShare
        id={publicShare}
        locale={locale}
        hasCharacter={!!char}
        onJoin={() => {
          setPublicShare(null);
          history.replaceState({}, '', location.pathname);
        }}
        onFriend={async (id) => {
          await act('friend.request', { targetId: id }, t('saved'));
          setPublicShare(null);
          history.replaceState({}, '', location.pathname);
          setPanel('feed');
          setSocialTab('friends');
        }}
      />
    );
  return (
    <main className={`app ${!char ? 'welcome' : ''}`}>
      <CityScene
        character={char || null}
        locale={locale}
        selected={selected}
        onPlace={(id) => {
          if (char) {
            setSelected(id);
            setPanel('map');
            setZone(getLocation(id)!.zone);
          }
        }}
        offset={offset}
        peers={multiplayer.peers}
        emotes={multiplayer.emotes}
        onEmote={multiplayer.sendEmote}
        emotesEnabled={multiplayer.status === 'online'}
        gardenStage={projectStage(world?.project)}
        billboards={{ subway: subwayAd, cafe: cafeAd }}
      />
      <div className="scene-vignette" />
      {char && (
        <button
          className="mobile-scene-residents"
          onClick={() => {
            setSocialTab('friends');
            setPanel('feed');
          }}
        >
          <Users size={12} />
          {multiplayer.status === 'online'
            ? `${multiplayer.peers.length + 1} · ${text('in scene', '同场景', '同場景', 'en escena')[locale]}`
            : text('Connecting…', '连接中…', '連線中…', 'Conectando…')[locale]}
        </button>
      )}
      <header className="topbar">
        <a className="brand" href="/" aria-label="American Dream Online home">
          <span className="brand-mark">
            <Home size={20} />
          </span>
          <span>
            AMERICAN DREAM
            <small>
              ONLINE <i /> NEW YORK
            </small>
          </span>
        </a>
        {char ? (
          <div className="wallets">
            <div className="wallet">
              <Coins size={18} />
              <div>
                <small>{t('cash')}</small>
                <strong>C$ {money(char.cash)}</strong>
              </div>
            </div>
            <div className="wallet voucher">
              <Ticket size={18} />
              <div>
                <small>{t('vouchers')}</small>
                <strong>C$ {money(char.vouchers)}</strong>
              </div>
            </div>
            <button
              className="profile-button"
              onClick={() => setPanel('settings')}
              aria-label={t('settings')}
              style={{ background: p?.appearance.shirt }}
            >
              {char.name[0]?.toUpperCase()}
            </button>
          </div>
        ) : (
          <select
            className="language-select"
            aria-label={t('uiLanguage')}
            value={locale}
            onChange={(e) => changeLocale(e.target.value as Locale)}
          >
            {LOCALES.map((l) => (
              <option key={l} value={l}>
                {languageNames[l]}
              </option>
            ))}
          </select>
        )}
      </header>
      {!ready ? (
        <div className="loading-screen">
          <LoaderCircle className="spin" />
          {t('loading')}
        </div>
      ) : !char ? (
        <Welcome
          locale={locale}
          user={user}
          emailAvailable={emailAvailable}
          initialMode={authMode}
          onAccountSupport={() => setAccountOpen(true)}
          onUser={setUser}
          onMessage={notify}
          onCharacter={async () => {
            const w = await refresh();
            setSelected(w.character.data.location);
          }}
          onError={(e) => notify(errorText(locale, e), true)}
        />
      ) : (
        <>
          <aside className="sidebar">
            <div className="greeting">
              <small>{t('newYork')}</small>
              <h1>{t('hello')}</h1>
              <p>
                <span className="live-dot" />
                {char.name} <span>·</span> {zones.find((z) => z.id === p?.zone)?.name[locale]}
              </p>
            </div>
            <nav>
              {menus.map((m) => (
                <button
                  className={panel === m.id ? 'active' : ''}
                  key={m.id}
                  onClick={() => openPanel(m.id)}
                  aria-label={t(m.label)}
                >
                  <m.icon size={19} />
                  <span className="nav-desktop-label">{t(m.label)}</span>
                  <span className="nav-mobile-label">{mobileMenu[m.id][locale]}</span>
                  {m.id === 'goals' && <small>{p!.goals.length}/7</small>}
                </button>
              ))}
            </nav>
            <div className="needs-card">
              <Need icon={Zap} label={t('energy')} value={p!.energy} color="#cfaa59" />
              <Need icon={Utensils} label={t('hunger')} value={p!.hunger} color="#7f9b75" />
            </div>
            <button className="sidebar-bottom" onClick={() => setPanel('settings')}>
              <Settings size={17} />
              <span>{t('settings')}</span>
              <Globe2 size={15} />
            </button>
          </aside>
          <div className="district-tabs">
            {zones.map((z) => (
              <button
                key={z.id}
                className={zone === z.id ? 'active' : ''}
                onClick={() => {
                  setZone(z.id);
                  setSelected(z.id === 'brooklyn' ? 'home' : z.id === 'midtown' ? 'cafe' : 'city-hall');
                  setPanel('map');
                }}
              >
                {z.name[locale]}
              </button>
            ))}
          </div>
          {panel === 'map' ? (
            <>
              {activeCoop && !p?.work && (
                <div className="quest-strip">
                  <Heart size={16} />
                  <div>
                    <small>
                      {activeCoop.npcPartner
                        ? 'Jordan · NPC'
                        : text('Neighborhood supper', '邻里合作', '鄰里合作', 'Colaboración')[locale]}{' '}
                      · {nextCoopIndex}/3
                    </small>
                    <strong>
                      {nextCoopIndex < 3
                        ? coopStepName(locale, ['gather', 'prepare', 'deliver'][nextCoopIndex]!)
                        : activeCoop.completedAt
                          ? text('Supper delivered', '晚餐已送达', '晚餐已送達', 'Cena entregada')[locale]
                          : text(
                              'Waiting for your partner',
                              '等待伙伴接力',
                              '等待夥伴接力',
                              'Esperando al compañero',
                            )[locale]}
                    </strong>
                  </div>
                  {nextCoopIndex < 3 ? (
                    <button
                      className="round-button"
                      aria-label={t('go')}
                      disabled={busy || !!p?.travel}
                      onClick={() => go(['bodega', 'home', 'park'][nextCoopIndex]!)}
                    >
                      <ArrowUpRight size={17} />
                    </button>
                  ) : activeCoop.completedAt ? (
                    <button
                      className="round-button"
                      aria-label={
                        text('Collect home vouchers', '领取家居券', '領取家居券', 'Recoger vales de hogar')[
                          locale
                        ]
                      }
                      disabled={busy || !!p?.cooldowns[`coop-claim:${utcDay()}`]}
                      onClick={() => act('coop.claim', { coopId: activeCoop.id }, t('saved'))}
                    >
                      <Ticket size={17} />
                    </button>
                  ) : null}
                </div>
              )}
              {!activeCoop && !p?.work && p?.counters.shifts === 0 && (
                <div className="quest-strip">
                  <BriefcaseBusiness size={16} />
                  <div>
                    <small>
                      {text('Your first step', '你的第一步', '你的第一步', 'Tu primer paso')[locale]}
                    </small>
                    <strong>
                      {
                        text(
                          'Your first shift: earn C$100',
                          '完成第一班工作，赚到 C$100',
                          '完成第一班工作，賺到 C$100',
                          'Tu primer turno: gana C$100',
                        )[locale]
                      }
                    </strong>
                  </div>
                  <button
                    className="round-button"
                    aria-label={t('shift')}
                    disabled={busy || !!p?.travel}
                    onClick={() => {
                      if (p?.location === 'cafe') {
                        setDialogCareer('service');
                        setChoice(0);
                      } else go('cafe');
                    }}
                  >
                    <ArrowUpRight size={17} />
                  </button>
                </div>
              )}
              <div className="destination-picker">
                <MapPin size={13} />
                <select
                  aria-label={t('map')}
                  value={selected}
                  onChange={(e) => {
                    setSelected(e.target.value);
                    setZone(getLocation(e.target.value)!.zone);
                  }}
                >
                  {locations.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name[locale]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="location-card">
                <div className="location-top">
                  <span className="location-symbol">
                    {selected === 'home' ? <Home size={23} /> : <MapPin size={23} />}
                  </span>
                  <div>
                    <small>{zones.find((z) => z.id === place.zone)?.name[locale]}</small>
                    <h2>{place.name[locale]}</h2>
                  </div>
                  <button className="round-button" aria-label={t('goals')} onClick={() => setPanel('goals')}>
                    <BookOpen size={18} />
                  </button>
                </div>
                {p?.location === selected && !p.travel ? (
                  locationActions()
                ) : (
                  <>
                    <p className="muted">{place.description[locale]}</p>
                    <button
                      className="button primary full"
                      disabled={busy || !!p?.travel || !!p?.work}
                      onClick={() => go(selected)}
                    >
                      {p?.travel ? t('traveling') : t('go')}{' '}
                      {p?.travel ? <LoaderCircle size={17} className="spin" /> : <ArrowUpRight size={17} />}
                    </button>
                  </>
                )}
              </div>
              <div className="map-caption">
                <span className={`connection-dot ${multiplayer.status}`} />
                <button
                  className="scene-people-button"
                  onClick={() => {
                    setSocialTab('friends');
                    setPanel('feed');
                  }}
                >
                  {multiplayer.status === 'online'
                    ? `${multiplayer.peers.length + 1} ${text(multiplayer.peers.length ? 'players in this scene' : 'player in this scene', '位玩家在同一场景', '位玩家在同一場景', multiplayer.peers.length ? 'jugadores en la escena' : 'jugador en la escena')[locale]}`
                    : multiplayer.status === 'waiting'
                      ? text(
                          'Live scene full · other activities remain available',
                          '实时场景已满，可先继续其他活动',
                          '即時場景已滿，可先繼續其他活動',
                          'Escena llena · puedes seguir con otras actividades',
                        )[locale]
                      : multiplayer.status === 'elsewhere'
                        ? text(
                            'Scene active in another tab',
                            '另一标签页正在显示街区',
                            '另一分頁正在顯示街區',
                            'Escena activa en otra pestaña',
                          )[locale]
                        : text('Connecting…', '正在连接街区…', '正在連線街區…', 'Conectando…')[locale]}
                </button>
                <i />
                <MapPin size={15} />
                <span>{p?.travel ? t('traveling') : t('tap')}</span>
                <i />
                NYC · 01
              </div>
            </>
          ) : (
            <section className={`drawer ${panel === 'home' ? 'wide' : ''}`}>
              <button className="drawer-close" aria-label={t('close')} onClick={() => setPanel('map')}>
                <X size={20} />
              </button>
              {panelBody()}
            </section>
          )}
          {p?.work && panel !== 'careers' && <div className="floating-work">{work()}</div>}
          <div className="mobile-status">
            <span>
              <Zap size={13} />
              {p?.energy}
            </span>
            <span>
              <Utensils size={13} />
              {p?.hunger}
            </span>
          </div>
        </>
      )}
      {dialogCareer && (
        <div className="modal-scrim" onClick={() => setDialogCareer(null)}>
          <section className="modal" onClick={(e) => e.stopPropagation()}>
            <button className="drawer-close" aria-label={t('close')} onClick={() => setDialogCareer(null)}>
              <X size={20} />
            </button>
            <div className="section-eyebrow">{t('choose')}</div>
            <h2>{careers.find((c) => c.id === dialogCareer)!.name[locale]}</h2>
            <p>{careers.find((c) => c.id === dialogCareer)!.prompt[locale]}</p>
            <div className="choices">
              {careers
                .find((c) => c.id === dialogCareer)!
                .choices.map((c, i) => (
                  <button key={i} className={choice === i ? 'selected' : ''} onClick={() => setChoice(i)}>
                    <span>{i + 1}</span>
                    <div>
                      {c[locale]}
                      <small>
                        +{workProfiles[dialogCareer]![i]!.xp} XP · −{workProfiles[dialogCareer]![i]!.energy}{' '}
                        {t('energy')}
                        {workProfiles[dialogCareer]![i]!.relation ? ` · +2 ${t('relationship')}` : ''}
                      </small>
                    </div>
                    {choice === i && <Check size={16} />}
                  </button>
                ))}
            </div>
            <p className="muted small">{t('shiftInfo')}</p>
            <button
              className="button primary full"
              disabled={busy}
              onClick={async () => {
                const r = await act('work.start', { career: dialogCareer, choice });
                if (r) setDialogCareer(null);
              }}
            >
              {t('shift')}
              <ArrowUpRight size={16} />
            </button>
          </section>
        </div>
      )}
      {shareOpen && char && (
        <ShareDialog
          locale={locale}
          character={char}
          onClose={() => setShareOpen(false)}
          onMessage={notify}
        />
      )}
      {visit && (
        <div className="modal-scrim">
          <section className="modal">
            <button className="drawer-close" aria-label={t('close')} onClick={() => setVisit(null)}>
              <X size={19} />
            </button>
            <div className="section-eyebrow">{t('home')}</div>
            <h2>{visit.name}</h2>
            <RoomScene decor={visit.decor} home={visit.home} locale={locale} />
          </section>
        </div>
      )}
      {reportId && (
        <div className="modal-scrim">
          <section className="modal">
            <button className="drawer-close" aria-label={t('close')} onClick={() => setReportId(null)}>
              <X />
            </button>
            <h2>{t('report')}</h2>
            <textarea
              maxLength={1000}
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
              aria-label={t('report')}
            />
            <button
              className="button primary full"
              disabled={busy || reportReason.trim().length < 3}
              onClick={async () => {
                const r = await act(
                  'report',
                  { postId: reportId, reason: reportReason, readingLocale: p?.readingLocale },
                  t('saved'),
                );
                if (r) {
                  setReportId(null);
                  setReportReason('');
                }
              }}
            >
              {t('send')}
            </button>
          </section>
        </div>
      )}
      {toast && (
        <div className={`toast ${toast.error ? 'error' : ''}`} role="status">
          {toast.error ? <X size={17} /> : <CheckCircle2 size={17} />}
          <span>{toast.message}</span>
          <button onClick={() => setToast(null)} aria-label={t('close')}>
            <X size={15} />
          </button>
        </div>
      )}
      {busy && (
        <div className="busy-indicator">
          <LoaderCircle size={16} className="spin" />
        </div>
      )}
    </main>
  );
}
function Need({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: typeof Zap;
  label: string;
  value: number;
  color: string;
}) {
  return (
    <div className="need">
      <Icon size={16} style={{ color }} />
      <span>{label}</span>
      <b>{value}</b>
      <div className="meter">
        <span style={{ width: `${value}%`, background: color }} />
      </div>
    </div>
  );
}
function Welcome({
  locale,
  user,
  emailAvailable,
  initialMode,
  onAccountSupport,
  onUser,
  onCharacter,
  onError,
  onMessage,
}: {
  locale: Locale;
  user: User | null;
  emailAvailable: boolean;
  initialMode: 'signup' | 'signin';
  onAccountSupport: () => void;
  onUser: (u: User) => void;
  onCharacter: () => Promise<void>;
  onError: (e: unknown) => void;
  onMessage: (s: string) => void;
}) {
  const t = (key: Word) => translate(locale, key);
  const [mode, setMode] = useState<'signup' | 'signin' | 'forgot'>(initialMode),
    [email, setEmail] = useState(''),
    [password, setPassword] = useState(''),
    [adultConfirmed, setAdultConfirmed] = useState(false),
    [name, setName] = useState(user?.name || ''),
    [background, setBackground] = useState<'graduate' | 'switcher' | 'founder'>('graduate'),
    [busy, setBusy] = useState(false);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!user && mode === 'signup' && !adultConfirmed) return;
    setBusy(true);
    try {
      if (mode === 'forgot') {
        if (!emailAvailable)
          throw new ApiError('EMAIL_UNAVAILABLE', 'Email is temporarily unavailable.', 503);
        await post('/auth/request-password-reset', { email, redirectTo: `${location.origin}/?reset=1` });
        onMessage(
          text(
            'If this account exists, recovery instructions are queued.',
            '如该账号存在，找回邮件已排队发送。',
            '若此帳號存在，復原郵件已排隊傳送。',
            'Si la cuenta existe, el mensaje está en cola.',
          )[locale],
        );
        return;
      }
      if (!user) {
        const r = await post(`/auth/${mode === 'signup' ? 'sign-up' : 'sign-in'}/email`, {
          email,
          password,
          ...(mode === 'signup' ? { name, adultConfirmed } : {}),
          rememberMe: true,
        });
        onUser(r.user);
        const s = await api('/session');
        if (s.characterId) await onCharacter();
      } else {
        await post('/character', { name, locale, background });
        await onCharacter();
      }
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="welcome-layout">
      <section className="welcome-copy">
        <span className="chapter-label">
          <span />
          {t('newYork')}
        </span>
        <h1>
          Small steps.
          <br />
          <em>Big dreams.</em>
        </h1>
        <p>{t('tagline')}</p>
        <div className="welcome-footnote">
          <span>
            <Coffee size={16} /> {text('Work & grow', '工作与成长', '工作與成長', 'Trabaja y crece')[locale]}
          </span>
          <span>
            <Home size={16} />
            {text('Make a home', '布置你的家', '布置你的家', 'Crea tu hogar')[locale]}
          </span>
          <span>
            <Users size={16} />
            {text('Find your people', '认识新邻居', '認識新鄰居', 'Conoce vecinos')[locale]}
          </span>
        </div>
      </section>
      <section className="welcome-card">
        <div className="section-eyebrow">YOUR NEW YORK STORY</div>
        <h2>{user ? t('start') : mode === 'signup' ? t('start') : t('continue')}</h2>
        <form onSubmit={submit}>
          {(user || mode === 'signup') && (
            <label>
              {t('name')}
              <input
                required
                minLength={2}
                maxLength={24}
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="nickname"
                placeholder="Alex"
              />
            </label>
          )}
          {!user ? (
            <>
              <label>
                {t('email')}
                <input
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  placeholder="you@example.com"
                />
              </label>
              {mode !== 'forgot' && (
                <label>
                  {t('password')}
                  <input
                    required
                    type="password"
                    minLength={mode === 'signup' ? 10 : 1}
                    maxLength={128}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                    placeholder="••••••••••"
                  />
                </label>
              )}
              {mode === 'signup' && (
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <input
                    type="checkbox"
                    required
                    checked={adultConfirmed}
                    onChange={(e) => setAdultConfirmed(e.target.checked)}
                    style={{ width: 'auto', height: 'auto', marginTop: 3, flexShrink: 0 }}
                  />
                  <span>
                    {
                      text(
                        'I am 18 or older.',
                        '我已年满 18 岁。',
                        '我已年滿 18 歲。',
                        'Confirmo que tengo 18 años o más.',
                      )[locale]
                    }
                  </span>
                </label>
              )}
            </>
          ) : (
            <>
              <p className="muted small">{t('background')}</p>
              <div className="background-options">
                {(['graduate', 'switcher', 'founder'] as const).map((b, i) => (
                  <button
                    type="button"
                    key={b}
                    className={background === b ? 'selected' : ''}
                    onClick={() => setBackground(b)}
                  >
                    <span>{['✦', '↗', '☕'][i]}</span>
                    {t(b)}
                    {background === b && <Check size={15} />}
                  </button>
                ))}
              </div>
              <p className="settlement-note">
                <Ticket size={17} />
                {t('settlement')}
              </p>
            </>
          )}
          <button
            className="button primary full large"
            disabled={busy || (!user && mode === 'signup' && !adultConfirmed)}
          >
            {busy ? (
              <LoaderCircle className="spin" size={18} />
            ) : (
              <>
                {user
                  ? t('begin')
                  : mode === 'signup'
                    ? t('signUp')
                    : mode === 'forgot'
                      ? text('Send recovery email', '发送找回邮件', '傳送復原郵件', 'Enviar recuperación')[
                          locale
                        ]
                      : t('signIn')}
                <ArrowUpRight size={18} />
              </>
            )}
          </button>
        </form>
        {!user && mode === 'signin' && (
          <button className="auth-switch" onClick={() => setMode('forgot')}>
            {text('Forgot your password?', '忘记密码？', '忘記密碼？', '¿Olvidaste la contraseña?')[locale]}
          </button>
        )}
        {!user && (
          <button className="auth-switch" onClick={() => setMode(mode === 'signup' ? 'signin' : 'signup')}>
            {t(mode === 'signup' ? 'already' : 'newHere')}
          </button>
        )}
        {user && (
          <button className="button soft full" onClick={onAccountSupport}>
            {
              text(
                'Account support and my data',
                '账号支持与我的数据',
                '帳號支援與我的資料',
                'Soporte de cuenta y mis datos',
              )[locale]
            }
          </button>
        )}
        <p className="muted small">
          <a href={`/privacy?lang=${locale}`} target="_blank" rel="noopener noreferrer">
            {text('Data and privacy', '数据与隐私说明', '資料與隱私說明', 'Datos y privacidad')[locale]}
          </a>{' '}
          ·{' '}
          <a href="/?privacy=receipt">
            {
              text('Private receipt lookup', '查询私密回执', '查詢私密回執', 'Consultar recibo privado')[
                locale
              ]
            }
          </a>
        </p>
        <div className="welcome-card-footer">
          <Leaf size={14} />
          {
            text(
              'A new life, at your own pace.',
              '按自己的节奏，开始一段新生活。',
              '按自己的步調，開始一段新生活。',
              'Una nueva vida, a tu ritmo.',
            )[locale]
          }
        </div>
      </section>
    </div>
  );
}
function PostCard({
  item,
  locale,
  readingLocale,
  currentId,
  busy,
  onAction,
}: {
  item: SocialPost;
  locale: Locale;
  readingLocale: Locale;
  currentId: string;
  busy: boolean;
  onAction: (type: string, p: Record<string, unknown>) => Promise<boolean | void>;
}) {
  const t = (key: Word) => translate(locale, key);
  const ref = useRef<HTMLElement>(null);
  const [translation, setTranslation] = useState<{ status: string; body?: string }>(),
    [original, setOriginal] = useState(false),
    [menu, setMenu] = useState(false),
    [editing, setEditing] = useState(false),
    [editBody, setEditBody] = useState(item.body);
  useEffect(() => {
    setTranslation(undefined);
    if (item.language === readingLocale) return;
    let cancelled = false;
    const controller = new AbortController();
    setOriginal(false);
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        observer.disconnect();
        setTranslation({ status: 'pending' });
        queuedTranslation(item.id, readingLocale, controller.signal)
          .then((r) => {
            if (!cancelled) setTranslation(r);
          })
          .catch(() => {
            if (!cancelled) setTranslation({ status: 'unavailable' });
          });
      },
      { rootMargin: '30px' },
    );
    if (ref.current) observer.observe(ref.current);
    return () => {
      cancelled = true;
      controller.abort();
      observer.disconnect();
    };
  }, [item.id, item.version, readingLocale]);
  return (
    <article className="post-card" ref={ref}>
      <header>
        <span className="post-avatar">{item.authorName[0]}</span>
        <div>
          <strong>{item.authorName}</strong>
          <small>
            {new Intl.DateTimeFormat(locale, {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            }).format(new Date(item.createdAt))}
          </small>
        </div>
        <button className="round-button" aria-label={t('postActions')} onClick={() => setMenu(!menu)}>
          ···
        </button>
      </header>
      {editing ? (
        <form
          className="post-edit"
          onSubmit={async (e) => {
            e.preventDefault();
            if (
              (await onAction('post.edit', { postId: item.id, body: editBody, language: locale })) !== false
            )
              setEditing(false);
          }}
        >
          <textarea
            aria-label={text('Edit original post', '编辑原文', '編輯原文', 'Editar original')[locale]}
            value={editBody}
            onChange={(e) => setEditBody(e.target.value)}
            minLength={1}
            maxLength={500}
            required
          />
          <div className="vote-buttons">
            <button className="button soft compact" disabled={busy || !editBody.trim()}>
              {text('Save changes', '保存修改', '儲存修改', 'Guardar cambios')[locale]}
            </button>
            <button type="button" className="button light compact" onClick={() => setEditing(false)}>
              {text('Cancel', '取消', '取消', 'Cancelar')[locale]}
            </button>
          </div>
        </form>
      ) : (
        <p>{translation?.status === 'ready' && !original ? translation.body : item.body}</p>
      )}

      {item.language !== readingLocale && (
        <button className="translation-label" onClick={() => setOriginal(!original)}>
          <Globe2 size={12} />
          {translation?.status === 'ready'
            ? original
              ? t('translated')
              : t('original')
            : translation?.status === 'pending'
              ? t('translatePending')
              : t('translationUnavailable')}
        </button>
      )}
      <footer>
        <button
          className={item.liked ? 'liked' : ''}
          disabled={busy}
          onClick={() => onAction('post.like', { postId: item.id, liked: !item.liked })}
        >
          <Heart size={15} fill={item.liked ? 'currentColor' : 'none'} />
          {item.likes || t('like')}
        </button>
        <span>{item.language === 'auto' ? '' : item.language}</span>
      </footer>
      {menu && (
        <div className="post-menu">
          {item.authorId === currentId ? (
            <>
              <button
                disabled={busy}
                onClick={() => {
                  setEditBody(item.body);
                  setEditing(true);
                  setMenu(false);
                }}
              >
                {text('Edit original', '编辑原文', '編輯原文', 'Editar original')[locale]}
              </button>
              <button disabled={busy} onClick={() => onAction('post.delete', { postId: item.id })}>
                {t('delete')}
              </button>
            </>
          ) : (
            <>
              <button disabled={busy} onClick={() => onAction('friend.request', { targetId: item.authorId })}>
                {t('addFriend')}
              </button>
              <button onClick={() => onAction('report', { postId: item.id })}>{t('report')}</button>
              <button disabled={busy} onClick={() => onAction('block', { targetId: item.authorId })}>
                {t('block')}
              </button>
            </>
          )}
        </div>
      )}
    </article>
  );
}
